import {
  queryOptions,
  useMutation,
  type UseMutationOptions,
} from '@tanstack/react-query'
import type { AxiosError } from 'axios'

import {
  activateLeague,
  activateLeagueMatch,
  createLeague,
  deleteLeague,
  finishLeague,
  getLeague,
  getLeagueContext,
  getLeagueMatches,
  getLeagueStandings,
  listLeagueAdminOptions,
  listLeagues,
  reassignLeague,
  setLeagueMatchAutoStart,
  setLeagueMatchWalkover,
  setLeagueTeamScore,
  updateLeague,
  updateLeagueMatchDate,
} from '@/modules/league/league.service'
import type {
  TCreateLeagueRequest,
  TLeagueDetail,
  TLeagueMatch,
  TUpdateLeagueRequest,
} from '@/modules/league/league.types'
import type { TAdminListParams } from '@/core/lib/table-query'

type TMutationError = AxiosError<{ code?: string; message?: string }>

export const leagueListQueryOptions = (params: TAdminListParams) =>
  queryOptions({
    queryKey: ['leagues', 'list', params],
    queryFn: () => listLeagues(params),
    staleTime: 1000 * 60,
  })

export const leagueDetailQueryOptions = (id: string, group?: number) =>
  queryOptions({
    queryKey: ['leagues', 'detail', id, group ?? null],
    queryFn: () => getLeague(id, group),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: 10_000,
  })

/**
 * Same league detail payload, no live polling. Use for bulk enrichment
 * (e.g. list-lobby maps lobby_id → league) where the 10s refetchInterval
 * on the detail page would multiply into N background requests.
 */
export const leagueDetailLiteQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['leagues', 'detail', id],
    queryFn: () => getLeague(id),
    staleTime: 1000 * 60,
  })

export const leagueStandingsQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['leagues', 'standings', id],
    queryFn: () => getLeagueStandings(id),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: 10_000,
  })

export const leagueMatchesQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ['leagues', 'matches', id],
    queryFn: () => getLeagueMatches(id),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchInterval: 10_000,
  })

export const leagueAdminOptionsQueryOptions = () =>
  queryOptions({
    queryKey: ['leagues', 'admin-options'],
    queryFn: listLeagueAdminOptions,
    staleTime: 1000 * 60 * 10,
  })

// One call backing the create/edit form: teams + admins + custom game modes.
// Use this instead of fetching those three separately.
export const leagueContextQueryOptions = () =>
  queryOptions({
    queryKey: ['leagues', 'context'],
    queryFn: getLeagueContext,
    staleTime: 1000 * 60 * 5,
  })

export const useCreateLeague = (
  options?: UseMutationOptions<
    TLeagueDetail,
    TMutationError,
    TCreateLeagueRequest
  >
) => useMutation({ mutationFn: createLeague, ...(options ?? {}) })

export const useUpdateLeague = (
  options?: UseMutationOptions<
    TLeagueDetail,
    TMutationError,
    { id: string; body: TUpdateLeagueRequest }
  >
) =>
  useMutation({
    mutationFn: ({ id, body }) => updateLeague(id, body),
    ...(options ?? {}),
  })

export const useDeleteLeague = (
  options?: UseMutationOptions<void, TMutationError, string>
) => useMutation({ mutationFn: deleteLeague, ...(options ?? {}) })

export const useReassignLeague = (
  options?: UseMutationOptions<TLeagueDetail, TMutationError, string>
) => useMutation({ mutationFn: reassignLeague, ...(options ?? {}) })

export const useActivateLeague = (
  options?: UseMutationOptions<TLeagueDetail, TMutationError, string>
) => useMutation({ mutationFn: activateLeague, ...(options ?? {}) })

export const useFinishLeague = (
  options?: UseMutationOptions<TLeagueDetail, TMutationError, string>
) => useMutation({ mutationFn: finishLeague, ...(options ?? {}) })

export const useActivateLeagueMatch = (
  options?: UseMutationOptions<
    TLeagueMatch,
    TMutationError,
    { leagueId: string; matchId: string }
  >
) =>
  useMutation({
    mutationFn: ({ leagueId, matchId }) =>
      activateLeagueMatch(leagueId, matchId),
    ...(options ?? {}),
  })

export const useUpdateLeagueMatchDate = (
  options?: UseMutationOptions<
    TLeagueMatch,
    TMutationError,
    { leagueId: string; matchId: string; scheduledAt: string; force?: boolean }
  >
) =>
  useMutation({
    mutationFn: ({ leagueId, matchId, scheduledAt, force }) =>
      updateLeagueMatchDate(leagueId, matchId, scheduledAt, force ?? false),
    ...(options ?? {}),
  })

export const useSetLeagueTeamScore = (
  options?: UseMutationOptions<
    TLeagueDetail,
    TMutationError,
    { leagueId: string; teamId: string; score: number }
  >
) =>
  useMutation({
    mutationFn: ({ leagueId, teamId, score }) =>
      setLeagueTeamScore(leagueId, teamId, score),
    ...(options ?? {}),
  })

export const useSetLeagueMatchWalkover = (
  options?: UseMutationOptions<
    TLeagueMatch,
    TMutationError,
    { leagueId: string; matchId: string; winnerTeamId: string }
  >
) =>
  useMutation({
    mutationFn: ({ leagueId, matchId, winnerTeamId }) =>
      setLeagueMatchWalkover(leagueId, matchId, winnerTeamId),
    ...(options ?? {}),
  })

export const useSetLeagueMatchAutoStart = (
  options?: UseMutationOptions<
    TLeagueMatch,
    TMutationError,
    { leagueId: string; matchId: string; enabled: boolean }
  >
) =>
  useMutation({
    mutationFn: ({ leagueId, matchId, enabled }) =>
      setLeagueMatchAutoStart(leagueId, matchId, enabled),
    ...(options ?? {}),
  })
